/**
 * The question's topic and Y&F pointer (slice B, P1-4): "Fotoelektrisk effekt · Y&F 38.1",
 * linking to the topic's row on `/library/topics#<topic>`. Practice only — the exam session has
 * its own problem view and never renders this. The link opens a new tab so the running set is
 * not left behind.
 *
 * `variant="chip"` sits above a question statement; `variant="readMore"` closes a solution
 * ("Läs mer: …"). Renders nothing until the shared topics query knows the topic.
 */
import { Link } from "react-router";
import { BookOpen } from "lucide-react";
import { useTopicsAndChapters } from "@client/api/topics";
import { useLocalizedText } from "@client/components/Localized";
import { formatBookRef } from "@client/features/content/bookRef";
import { useT } from "@client/i18n";

export interface TopicChipProps {
  topicId: string;
  variant?: "chip" | "readMore";
  className?: string;
}

export function TopicChip({ topicId, variant = "chip", className }: TopicChipProps) {
  const t = useT();
  const localize = useLocalizedText();
  const topics = useTopicsAndChapters();
  const topic = topics.data?.topics.find((candidate) => candidate.id === topicId);
  if (!topic) return null;

  const label = [localize(topic.name), formatBookRef(topic.book, t)]
    .filter((part): part is string => !!part)
    .join(" · ");
  const href = `/library/topics#${encodeURIComponent(topic.id)}`;
  const newTab = <span className="sr-only"> {t("practice.topicChip.newTab")}</span>;

  if (variant === "readMore") {
    return (
      <p className={className ?? "mt-3 text-sm text-ink-700 dark:text-ink-200"}>
        {t("practice.topicChip.readMore")}{" "}
        <Link
          to={href}
          target="_blank"
          rel="noopener"
          className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
        >
          {label}
          {newTab}
        </Link>
      </p>
    );
  }
  return (
    <Link
      to={href}
      target="_blank"
      rel="noopener"
      className={
        className ??
        "mb-3 inline-flex max-w-full items-center gap-1.5 rounded-full border border-ink-200 bg-ink-50 px-2.5 py-0.5 text-xs font-medium text-ink-700 hover:border-accent-400 hover:text-accent-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 dark:border-ink-700 dark:bg-ink-800/60 dark:text-ink-200 dark:hover:text-accent-200"
      }
    >
      <BookOpen className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
      {newTab}
    </Link>
  );
}
